import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin, Globe, Lock, AlertTriangle, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Session } from '@supabase/supabase-js';
import { format } from 'date-fns';
import { EventType, EVENT_TYPES } from '../types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface AddEventFormProps {
  onClose: () => void;
  session: Session;
  selectedDate?: Date;
}

interface Coordinates {
  lat: number;
  lng: number;
}

export function AddEventForm({ onClose, session, selectedDate }: AddEventFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '');
  const [isPrivate, setIsPrivate] = useState(false);
  const [venue, setVenue] = useState('');
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [eventType, setEventType] = useState<EventType>('convention');
  const [showPreview, setShowPreview] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const mapsApiLoadedRef = useRef(false);
  const formSubmittedRef = useRef(false);

  // Initialize map when coordinates change and Google Maps is loaded
  useEffect(() => {
    // Check if Google Maps API is already loaded
    if (window.google?.maps) {
      mapsApiLoadedRef.current = true;
    }

    // Wait for both map to be shown, coordinates to be set, and Google Maps to be loaded
    if (!showMap || !coordinates || !window.google?.maps || !mapRef.current) return;

    const mapOptions = {
      center: coordinates,
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    };

    const map = new window.google.maps.Map(mapRef.current, mapOptions);
    googleMapRef.current = map;

    const marker = new window.google.maps.Marker({
      position: coordinates,
      map,
      draggable: true,
      title: venue || 'Event location'
    });
    markerRef.current = marker;

    // Update coordinates when marker is dragged
    marker.addListener('dragend', () => {
      const position = marker.getPosition();
      if (position) {
        setCoordinates({
          lat: position.lat(),
          lng: position.lng()
        });
      }
    });

    // Add click listener to map
    map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        marker.setPosition(e.latLng);
        setCoordinates({
          lat: e.latLng.lat(),
          lng: e.latLng.lng()
        });
      }
    });

  }, [showMap, coordinates, venue]);

  // Check if venue is set but coordinates aren't, and Google Maps is loaded
  useEffect(() => {
    if (venue && !coordinates && window.google?.maps) {
      searchLocation(venue);
    }
  }, [venue, coordinates]);

  const searchLocation = async (address: string) => {
    if (!address || !window.google?.maps) return;

    setIsSearchingLocation(true);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
            resolve(results);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });

      if (result && result[0]?.geometry?.location) {
        const location = result[0].geometry.location;
        const newCoords = {
          lat: location.lat(),
          lng: location.lng()
        };
        setCoordinates(newCoords);
        setShowMap(true);

        // Update marker and map center if they exist
        if (markerRef.current && googleMapRef.current) {
          markerRef.current.setPosition(location);
          googleMapRef.current.setCenter(location);
        }
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
      setError('Could not find location. Please try a different address.');
    } finally {
      setIsSearchingLocation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting || formSubmittedRef.current) return;
    
    if (!title || !description || !date) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setIsSubmitting(true);
      formSubmittedRef.current = true;
      setError(null);

      const { error: submitError } = await supabase
        .from('events')
        .insert([{
          user_id: session.user.id,
          title,
          description,
          date,
          is_private: isPrivate,
          venue: venue || null,
          venue_lat: coordinates?.lat || null,
          venue_lng: coordinates?.lng || null,
          event_type: eventType
        }]);

      if (submitError) throw submitError;

      // Mark as success and close after a short delay
      setIsSuccess(true);
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error: any) {
      console.error('Error adding event:', error);
      setError(error.message);
      setIsSubmitting(false);
      formSubmittedRef.current = false;
    }
  };

  // Get event type color
  const getEventTypeColor = (type: EventType): string => {
    switch (type) {
      case 'convention': return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300';
      case 'church': return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300';
      case 'home group': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300';
      case 'prayer meeting': return 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300';
      case 'business': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      case 'family': return 'bg-rose-100 dark:bg-rose-900 text-rose-800 dark:text-rose-300';
      case 'other': return 'bg-cyan-100 dark:bg-cyan-900 text-cyan-800 dark:text-cyan-300';
      default: return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          disabled={isSubmitting}
        >
          <X size={24} />
        </button>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          Add New Event
        </h2>

        {isSuccess ? (
          <div className="bg-green-50 dark:bg-green-900/50 p-4 rounded-lg text-center">
            <div className="text-green-600 dark:text-green-400 text-lg font-medium mb-2">
              Event Added Successfully!
            </div>
            <p className="text-green-700 dark:text-green-300">
              Your event has been added to the calendar.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Title *
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Event title"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Date *
              </label>
              <input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="event-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Event Type *
              </label>
              <select
                id="event-type"
                value={eventType}
                onChange={(e) => setEventType(e.target.value as EventType)}
                className="mt-1 block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                required
                disabled={isSubmitting}
              >
                {EVENT_TYPES.map(type => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
              <div className="mt-2">
                <span className={`inline-block px-2 py-1 text-xs rounded-full ${getEventTypeColor(eventType)}`}>
                  {eventType.charAt(0).toUpperCase() + eventType.slice(1)}
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description * (Markdown supported)
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                className="mt-1 block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
                placeholder="Event description. You can use Markdown formatting:
**bold text** or __bold text__
*italic text* or _italic text_
# Heading 1
## Heading 2
- Bullet point
1. Numbered list
[Link text](https://example.com)
> Blockquote"
                required
                disabled={isSubmitting}
              />
              <div className="flex justify-end mt-1">
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>
              
              {showPreview && (
                <div className="mt-2 p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview:</h3>
                  <div className="prose dark:prose-invert max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {description}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="venue" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Venue
              </label>
              <div className="mt-1 flex">
                <input
                  id="venue"
                  type="text"
                  value={venue}
                  onChange={(e) => setVenue(e.target.value)}
                  className="block w-full px-3 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Event location"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => searchLocation(venue)}
                  disabled={!venue || isSearchingLocation || !window.google?.maps || isSubmitting}
                  className="px-3 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MapPin size={16} />
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter an address to show on Google Maps
              </p>
            </div>

            {showMap && coordinates && (
              <div className="mt-2">
                <div 
                  ref={mapRef} 
                  className="w-full h-64 rounded-md border border-gray-300 dark:border-gray-600"
                ></div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Click on the map or drag the marker to adjust the location
                </p>
              </div>
            )}

            <div className="flex items-center">
              <input
                id="is-private"
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={isSubmitting}
              />
              <label htmlFor="is-private" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                {isPrivate ? (
                  <span className="flex items-center">
                    <Lock size={16} className="mr-1 text-amber-600 dark:text-amber-400" />
                    Private (only visible to you)
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Globe size={16} className="mr-1 text-green-600 dark:text-green-400" />
                    Public (visible to everyone)
                  </span>
                )}
              </label>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900 text-red-600 dark:text-red-300 p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Adding...
                  </>
                ) : 'Add Event'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}